import { useCallback, useEffect, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import AddBoxTwoToneIcon from "@mui/icons-material/AddBoxTwoTone";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import swal from "sweetalert";
import { logEventos } from "../../../services/services";
import Navbar from "../../Navbar";
import { formatCuit } from "../pago_proveedores/utils";
import {
  emailListoParaEnvio,
  enviarNotificacionEstadoProveedor,
  esEmailValido,
  helperTextCorreoAlta,
  helperTextCorreoBaja,
  obtenerEmailMaestro,
  resolverEmailParaEnvio,
} from "./altaProveedores.correo";
import CampoCorreoProveedor from "./CampoCorreoProveedor";
import ModalAltaProveedor from "./ModalAltaProveedor";
import {
  getProveedorByCuit,
  listProveedoresAuth,
  normalizarCuit,
  updateProveedorEstado,
} from "./altaProveedores.service";

const esActivo = (valor) => Number(valor) === 1;

const resolverEstadoProveedor = (activo, mustChangePassword) => {
  const activoNum = esActivo(activo) ? 1 : 0;
  const debeCambiarPass = Number(mustChangePassword) === 1 ? 1 : 0;

  if (activoNum === 0) return "Baja";
  if (debeCambiarPass === 1) return "Pendiente";
  return "Alta";
};

const chipPropsPorEstado = (estado) => {
  switch (estado) {
    case "Alta":
      return { color: "success", variant: "filled" };
    case "Pendiente":
      return { color: "warning", variant: "filled" };
    default:
      return { color: "default", variant: "outlined" };
  }
};

const normalizarFila = (row) => {
  const activo = esActivo(row.activo ?? 1) ? 1 : 0;
  const must_change_password =
    Number(row.must_change_password ?? 0) === 1 ? 1 : 0;

  return {
    cuit: normalizarCuit(row.cuit ?? row.nro_cuit ?? row.username ?? ""),
    razon_social: row.razon_social ?? row.denominacion ?? "",
    email: row.email?.trim() || "",
    activo,
    must_change_password,
    estado: resolverEstadoProveedor(activo, must_change_password),
    created_at: row.created_at ?? null,
  };
};

const formatearFecha = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return "—";
  return fecha.toLocaleString("es-AR");
};

export default function AltaProveedores() {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmacion, setConfirmacion] = useState({
    open: false,
    proveedor: null,
    activo: null,
  });
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  const [emailMaestro, setEmailMaestro] = useState("");
  const [emailManual, setEmailManual] = useState("");
  const [errorEmail, setErrorEmail] = useState("");
  const [cargandoEmail, setCargandoEmail] = useState(false);

  const emailParaEnvio = resolverEmailParaEnvio(emailMaestro, emailManual);
  const emailListo = emailListoParaEnvio(emailMaestro, emailManual);

  const cargarListado = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { rows } = await listProveedoresAuth();
      setProveedores(rows.map(normalizarFila));
    } catch (err) {
      setError(err.message || "Error al cargar el listado");
      setProveedores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    logEventos(
      "acceso al menú Alta Proveedores",
      "Proveedores",
      "Alta Proveedores",
      localStorage.getItem("user")
    );
  }, []);

  useEffect(() => {
    cargarListado();
  }, [cargarListado]);

  const handleAltaExitosa = () => {
    cargarListado();
  };

  const abrirConfirmacionEstado = (proveedor, activo) => {
    setEmailMaestro("");
    setEmailManual("");
    setErrorEmail("");
    setConfirmacion({ open: true, proveedor, activo });
  };

  const cerrarConfirmacionEstado = () => {
    if (cambiandoEstado) return;
    setEmailMaestro("");
    setEmailManual("");
    setErrorEmail("");
    setConfirmacion({ open: false, proveedor: null, activo: null });
  };

  useEffect(() => {
    if (!confirmacion.open || !confirmacion.proveedor?.cuit) {
      return;
    }

    let cancelled = false;

    const cargarEmailProveedor = async () => {
      setCargandoEmail(true);
      try {
        const proveedor = await getProveedorByCuit(confirmacion.proveedor.cuit);
        if (cancelled) return;
        const correo = obtenerEmailMaestro(proveedor);
        if (correo) {
          setEmailMaestro(correo);
        }
      } catch {
        // Si no hay email en maestro, el usuario lo ingresa manualmente.
      } finally {
        if (!cancelled) {
          setCargandoEmail(false);
        }
      }
    };

    cargarEmailProveedor();

    return () => {
      cancelled = true;
    };
  }, [confirmacion.open, confirmacion.proveedor?.cuit]);

  const handleConfirmarCambioEstado = async () => {
    const { proveedor, activo } = confirmacion;
    if (!proveedor || activo === null) return;

    if (!emailListo) {
      if (!emailMaestro && !esEmailValido(emailManual)) {
        setErrorEmail(
          activo === 1
            ? "Ingrese un correo válido para notificar al proveedor del alta"
            : "Ingrese un correo válido para notificar al proveedor de la baja"
        );
      }
      return;
    }

    setCambiandoEstado(true);
    try {
      const resultado = await updateProveedorEstado(
        proveedor.cuit,
        activo,
        emailParaEnvio
      );
      await enviarNotificacionEstadoProveedor({
        email: emailParaEnvio,
        cuit: proveedor.cuit,
        razonSocial: proveedor.razon_social || "",
        tipo: activo === 1 ? "reactivacion" : "baja",
      });
      await swal({
        title: activo === 1 ? "Alta exitosa" : "Baja exitosa",
        text:
          resultado?.msg ||
          `El proveedor fue dado de ${activo === 1 ? "alta" : "baja"} correctamente.`,
        icon: "success",
        closeOnClickOutside: false,
      });
      cerrarConfirmacionEstado();
      cargarListado();
    } catch (err) {
      swal({
        title: "No se pudo actualizar el estado",
        text: err.message || "Ocurrió un error al cambiar el estado del proveedor",
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setCambiandoEstado(false);
    }
  };

  const etiquetaAccionEstado = (activo) =>
    esActivo(activo) ? "Dar de baja" : "Dar de alta";

  const textoConfirmacionEstado = () => {
    const { proveedor, activo } = confirmacion;
    if (!proveedor) return "";

    const accion = activo === 1 ? "alta" : "baja";
    const nombre =
      proveedor.razon_social?.trim() || formatCuit(proveedor.cuit);

    return `¿Está seguro que desea dar de ${accion} al proveedor ${nombre}?`;
  };

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { minWidth: "18rem", backgroundColor: "#ffffff" },
    variant: "outlined",
    color: "primary",
    size: "small",
  };

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    pagination: { pageSize: 10, pageIndex: 0 },
  };

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      borderColor: "black",
    },
  };

  const columns = useMemo(
    () => [
      {
        header: "CUIT",
        accessorKey: "cuit",
        Cell: ({ cell }) => formatCuit(cell.getValue()),
      },
      { header: "Razón social", accessorKey: "razon_social" },
      {
        header: "Correo",
        accessorKey: "email",
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        header: "Estado",
        accessorKey: "estado",
        size: 120,
        Cell: ({ row }) => {
          const { estado } = row.original;
          const chipProps = chipPropsPorEstado(estado);
          return (
            <Chip
              label={estado}
              size="small"
              title={
                estado === "Pendiente"
                  ? "Alta sin activar cuenta"
                  : undefined
              }
              {...chipProps}
            />
          );
        },
      },
      {
        header: "Fecha alta",
        accessorKey: "created_at",
        Cell: ({ cell }) => formatearFecha(cell.getValue()),
      },
    ],
    []
  );

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#FAFBFC" }}>
      <Navbar />
      <Container
        maxWidth="xl"
        sx={{ pt: "20px", pb: "100px", px: 2, position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h5" component="h1" fontWeight={700}>
            Alta Proveedores
          </Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddBoxTwoToneIcon />}
              onClick={() => setModalOpen(true)}
            >
              Dar de alta
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={2}
          sx={{
            p: 2,
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
          }}
        >
          <MaterialReactTable
            columns={columns}
            data={proveedores}
            state={{ isLoading: loading }}
            initialState={initialState}
            localization={MRT_Localization_ES}
            muiSearchTextFieldProps={muiSearchTextFieldProps}
            muiTableHeadCellProps={tableHeadCellProps}
            muiTableBodyCellProps={{
              align: "left",
              sx: { borderColor: "black" },
            }}
            enableColumnResizing
            enableColumnActions={false}
            enableColumnFilters={false}
            enableHiding={false}
            enableDensityToggle={false}
            enableColumnDragging={false}
            enableStickyFooter
            enableGlobalFilter
            enableFullScreenToggle={false}
            layoutMode="grid"
            enableRowActions
            positionActionsColumn="last"
            displayColumnDefOptions={{
              "mrt-row-actions": {
                muiTableBodyCellProps: {
                  onClick: (event) => event.stopPropagation(),
                },
              },
            }}
            renderRowActionMenuItems={({ closeMenu, row }) => {
              const activo = row.original.activo;
              const esAlta = esActivo(activo);
              const nuevoActivo = esAlta ? 0 : 1;

              return [
                <MenuItem
                  key="cambiar-estado"
                  onClick={(e) => {
                    e.stopPropagation();
                    abrirConfirmacionEstado(row.original, nuevoActivo);
                    closeMenu();
                  }}
                >
                  <ListItemIcon>
                    {esAlta ? (
                      <PersonOffIcon color="error" />
                    ) : (
                      <PersonAddAltIcon color="primary" />
                    )}
                  </ListItemIcon>
                  {etiquetaAccionEstado(activo)}
                </MenuItem>,
              ];
            }}
          />
        </Paper>

        <ModalAltaProveedor
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={handleAltaExitosa}
        />

        <Dialog
          open={confirmacion.open}
          onClose={cerrarConfirmacionEstado}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {confirmacion.activo === 1 ? "Confirmar alta" : "Confirmar baja"}
          </DialogTitle>
          <DialogContent>
            <Typography>{textoConfirmacionEstado()}</Typography>
            {confirmacion.proveedor?.cuit ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                CUIT: {formatCuit(confirmacion.proveedor.cuit)}
              </Typography>
            ) : null}
            {cargandoEmail ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Buscando correo del proveedor...
                </Typography>
              </Box>
            ) : (
              <CampoCorreoProveedor
                emailMaestro={emailMaestro}
                emailManual={emailManual}
                onEmailManualChange={(valor) => {
                  setEmailManual(valor);
                  setErrorEmail("");
                }}
                errorEmail={errorEmail}
                disabled={cambiandoEstado}
                helperText={
                  confirmacion.activo === 1
                    ? helperTextCorreoAlta
                    : helperTextCorreoBaja
                }
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={cerrarConfirmacionEstado}
              color="inherit"
              disabled={cambiandoEstado}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color={confirmacion.activo === 1 ? "primary" : "error"}
              onClick={handleConfirmarCambioEstado}
              disabled={cambiandoEstado || cargandoEmail || !emailListo}
              startIcon={
                cambiandoEstado ? <CircularProgress size={18} color="inherit" /> : null
              }
            >
              {cambiandoEstado
                ? "Procesando..."
                : confirmacion.activo === 1
                  ? "Dar de alta"
                  : "Dar de baja"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}
